import { Component, EventEmitter, Input, Output, OnDestroy, OnInit } from '@angular/core';
import { GeothermalService } from '../services/geothermal.service';
import { UnitConversionService, UNITS } from 'src/app/shared/services/unit-conversion.service';
import { getMaxMinValidator } from '../geothermal-validators.constants';
import { UnitSystem } from 'src/app/shared/enums/unit-system.enum';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-geothermal-ranges-modal',
    templateUrl: './geothermal-ranges-modal.component.html',
    styleUrls: ['./geothermal-ranges-modal.component.css']
})
export class GeothermalRangesModalComponent implements OnInit, OnDestroy {
    @Input() show: boolean = false;
    @Output() close = new EventEmitter<void>();

    unitSystem: UnitSystem = UnitSystem.USC;
    private subUnitSystem: Subscription;

    // Precalculated range values
    modulusEMin: number;
    modulusEMax: number;
    modulusEUnit: string;
    coeffThermalExpMax: number;
    coeffThermalExpUnit: string;
    tempDeratingMax: number;
    tempDeratingUnit: string;
    deltaLPreloadMin: number;
    deltaLPreloadMax: number;
    deltaLPreloadUnit: string;
    outsideDiameterMin: number;
    outsideDiameterMax: number;
    outsideDiameterUnit: string;
    nominalWeightMin: number;
    nominalWeightMax: number;
    nominalWeightUnit: string;
    insideDiameterMin: number;
    insideDiameterMax: number;
    insideDiameterUnit: string;
    smysMin: number;
    smysMax: number;
    smysUnit: string;
    holeIdMin: number;
    holeIdMax: number;
    holeIdUnit: string;
    densityMin: number;
    densityMax: number;
    densityUnit: string;
    insidePressureMin: number;
    insidePressureMax: number;
    outsidePressureMin: number;
    outsidePressureMax: number;
    pressureUnit: string;
    avgTempMin: number;
    avgTempMax: number;
    avgTempUnit: string;

    constructor(
        private geothermalService: GeothermalService,
        public unitConversion: UnitConversionService
    ) { }

    ngOnInit() {
        this.subUnitSystem = this.geothermalService.unitSystem$.subscribe(unit => {
            this.unitSystem = unit;
            this.setRanges();
        });
        this.setRanges();
    }

    setRanges() {
        // Modulus of elasticity, E
        let modulusEMin = getMaxMinValidator('MODULUS_E_MIN', this.unitSystem);
        let modulusEMax = getMaxMinValidator('MODULUS_E_MAX', this.unitSystem);
        this.modulusEMin = modulusEMin;
        this.modulusEMax = modulusEMax;
        this.modulusEUnit = this.unitSystem === UnitSystem.USC ? UNITS.PRESSURE_PSI : UNITS.PRESSURE_MPA;
        // Coeff of thermal Exp
        let coeffThermalExpMax = getMaxMinValidator('THERMAL_EXP_MAX', this.unitSystem);
        this.coeffThermalExpMax = coeffThermalExpMax;
        this.coeffThermalExpUnit = this.unitSystem === UnitSystem.USC ? UNITS.THERMAL_EXP_F_1 : UNITS.THERMAL_EXP_C;
        // Temp. Derating
        let tempDeratingMax = getMaxMinValidator('TEMP_DERATING_MAX', this.unitSystem);
        this.tempDeratingMax = tempDeratingMax;
        this.tempDeratingUnit = this.unitSystem === UnitSystem.USC ? UNITS.THERMAL_EXP_F : UNITS.THERMAL_EXP_C;
        // Δ L Pre-load
        let deltaLPreloadMin = getMaxMinValidator('DELTA_L_PRELOAD_MIN', this.unitSystem);
        let deltaLPreloadMax = getMaxMinValidator('DELTA_L_PRELOAD_MAX', this.unitSystem);
        this.deltaLPreloadMin = deltaLPreloadMin;
        this.deltaLPreloadMax = deltaLPreloadMax;
        this.deltaLPreloadUnit = this.unitSystem === UnitSystem.USC ? UNITS.LENGTH_FT : UNITS.LENGTH_M;
        // Outside Diameter
        let outsideDiameterMin = getMaxMinValidator('OUTSIDE_DIAMETER_MIN', this.unitSystem);
        let outsideDiameterMax = getMaxMinValidator('OUTSIDE_DIAMETER_MAX', this.unitSystem);
        this.outsideDiameterMin = outsideDiameterMin;
        this.outsideDiameterMax = outsideDiameterMax;
        this.outsideDiameterUnit = this.unitSystem === UnitSystem.USC ? UNITS.LENGTH_IN : UNITS.LENGTH_MM;
        // Nominal Weight
        let nominalWeightMin = getMaxMinValidator('NOMINAL_WEIGHT_MIN', this.unitSystem);
        let nominalWeightMax = getMaxMinValidator('NOMINAL_WEIGHT_MAX', this.unitSystem);
        this.nominalWeightMin = nominalWeightMin;
        this.nominalWeightMax = nominalWeightMax;
        this.nominalWeightUnit = this.unitSystem === UnitSystem.USC ? UNITS.MASS_PER_LENGTH_LBFT : UNITS.MASS_PER_LENGTH_KGM;
        // Inside Diameter
        let insideDiameterMin = getMaxMinValidator('INSIDE_DIAMETER_MIN', this.unitSystem);
        let insideDiameterMax = getMaxMinValidator('INSIDE_DIAMETER_MAX', this.unitSystem);
        this.insideDiameterMin = insideDiameterMin;
        this.insideDiameterMax = insideDiameterMax;
        this.insideDiameterUnit = this.unitSystem === UnitSystem.USC ? UNITS.LENGTH_IN : UNITS.LENGTH_MM;
        // SMYS
        let smysMin = getMaxMinValidator('SMYS_MIN', this.unitSystem);
        let smysMax = getMaxMinValidator('SMYS_MAX', this.unitSystem);
        this.smysMin = smysMin;
        this.smysMax = smysMax;
        this.smysUnit = this.unitSystem === UnitSystem.USC ? UNITS.PRESSURE_PSI : UNITS.PRESSURE_MPA;
        // Hole ID
        let holeIdMin = getMaxMinValidator('HOLE_ID_MIN', this.unitSystem);
        let holeIdMax = getMaxMinValidator('HOLE_ID_MAX', this.unitSystem);
        this.holeIdMin = holeIdMin;
        this.holeIdMax = holeIdMax;
        this.holeIdUnit = this.unitSystem === UnitSystem.USC ? UNITS.LENGTH_IN : UNITS.LENGTH_MM;
        // Density
        let densityMin = getMaxMinValidator('DENSITY_MIN', this.unitSystem);
        let densityMax = getMaxMinValidator('DENSITY_MAX', this.unitSystem);
        this.densityMin = densityMin;
        this.densityMax = densityMax;
        this.densityUnit = this.unitSystem === UnitSystem.USC ? UNITS.DENSITY_PPG : UNITS.DENSITY_KGM3;
        // Pressure
        let insidePressureMin = getMaxMinValidator('PRESSURE_MIN', this.unitSystem);
        let insidePressureMax = getMaxMinValidator('PRESSURE_MAX_IN', this.unitSystem);
        let outsidePressureMin = getMaxMinValidator('PRESSURE_MIN', this.unitSystem);
        let outsidePressureMax = getMaxMinValidator('PRESSURE_MAX_OUT', this.unitSystem);
        this.insidePressureMin = insidePressureMin;
        this.insidePressureMax = insidePressureMax;
        this.outsidePressureMin = outsidePressureMin;
        this.outsidePressureMax = outsidePressureMax;
        this.pressureUnit = this.unitSystem === UnitSystem.USC ? UNITS.PRESSURE_PSI : UNITS.PRESSURE_MPA;
        // Avg Temperature
        let avgTempMin = getMaxMinValidator('TEMPERATURE_MIN', this.unitSystem);
        let avgTempMax = getMaxMinValidator('TEMPERATURE_MAX', this.unitSystem);
        this.avgTempMin = avgTempMin;
        this.avgTempMax = avgTempMax;
        this.avgTempUnit = this.unitSystem === UnitSystem.USC ? UNITS.TEMPERATURE_F : UNITS.TEMPERATURE_C;
    }

    ngOnDestroy() {
        if (this.subUnitSystem) this.subUnitSystem.unsubscribe();
    }

    closeModal() {
        this.close.emit();
    }

    get densityUnitFormatted(): string {
        if (this.densityUnit === UNITS.DENSITY_KGM3) {
            return 'kg/m<sup>3</sup>';
        }
        return this.densityUnit;
    }
}
